import { DEFAULT_DIALOG_CONFIG, DialogModule } from '@angular/cdk/dialog'
import { IMAGE_CONFIG } from '@angular/common'
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http'
import {
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	mergeApplicationConfig,
	provideAppInitializer,
	provideEnvironmentInitializer,
} from '@angular/core'
import {
	provideClientHydration,
	withEventReplay,
	withHttpTransferCacheOptions,
	withNoIncrementalHydration,
} from '@angular/platform-browser'
import { provideRouter, withViewTransitions } from '@angular/router'
import { AppConfigService, SupportedAssetDetectionService } from '@mos/client/shared/app-config'
import { apiNotificationInterceptor } from '@mos/client/web/api-notification/lib/api-notification.interceptor'
import { ApiNotificationService } from '@mos/client/web/api-notification/lib/api-notification.service'
import { EventService } from '@mos/client/web/event/lib/event.service'
import { TrackingService } from '@mos/client/web/tracking/lib/tracking.service'
import { AcquisitionRepository, apiInterceptor } from '@mos/shared/auth'
import { appRoutes } from './app.routes'

import { BankService } from '@mos/client/shared/bank/services/bank.service'
import { WebBankService } from '@mos/client/shared/bank/services/web-bank.service'
import { ConsentService } from '@mos/client/shared/consent/consent.service'
import { WebConsentService } from '@mos/client/shared/consent/web-consent.service'
import { LocalStorageStrategy } from '@mos/client/shared/storage/strategies/local-storage.strategy'
import { StorageStrategy } from '@mos/client/shared/storage/strategies/storage.strategy'
import { viewTransitionsOptions } from '@mos/client/web/core/routing/view-transitions'
import { AdRewardService } from '@mos/client/web/shop/data-access/services/ads/ad-reward.service'
import { WebAdRewardService } from '@mos/client/web/shop/data-access/services/ads/web-ad-reward.service'
import { CookieService } from 'ngx-cookie-service'
import { ChunkErrorHandler } from './chunk-error-handler'

export const appCommonConfig: ApplicationConfig = {
	providers: [
		provideRouter(appRoutes, withViewTransitions(viewTransitionsOptions)),
		provideHttpClient(withInterceptors([apiInterceptor, apiNotificationInterceptor]), withFetch()),
		importProvidersFrom([DialogModule]),
		AppConfigService,
		SupportedAssetDetectionService,
		ApiNotificationService,
		provideAppInitializer(() => {
			const initializerFn = (
				(configService: AppConfigService) => () =>
					configService.init('web')
			)(inject(AppConfigService))
			return initializerFn()
		}),
		{
			provide: IMAGE_CONFIG,
			useValue: {
				disableImageSizeWarning: true,
			},
		},
		{
			provide: DEFAULT_DIALOG_CONFIG,
			useValue: {
				hasBackdrop: true,
				backdropClass: 'mos-dialog-backdrop',
				closeOnNavigation: true,
				disableClose: true,
			},
		},
		{
			provide: BankService,
			useClass: WebBankService,
		},
		{
			provide: AdRewardService,
			useClass: WebAdRewardService,
		},
		{
			provide: ConsentService,
			useClass: WebConsentService,
		},
		{
			provide: StorageStrategy,
			useClass: LocalStorageStrategy,
		},
		provideClientHydration(
			withEventReplay(),
			// We don't want to cache the config.json file during SSG compilation
			// see https://angular.io/api/platform-browser/provideClientHydration
			withHttpTransferCacheOptions({
				filter: (request) => !request.url.includes('/config.json'),
			}),
			withNoIncrementalHydration(),
		),
		{
			// GTM provider
			provide: 'googleTagManagerId',
			useValue: 'GTM-MLDNC39G',
		},
		CookieService,
		// AdSense disabled for now - restore this block (and the `environment` import) to re-enable it.
		//
		// Runs during prerendering too (not SSR-excluded): the AdSense/Ad Placement verification
		// script needs to land in the prerendered landing page's static HTML, since Google's
		// site-verification crawl reads that output rather than executing client JS.
		// provideEnvironmentInitializer(() => {
		// 	const adRewardService = inject(AdRewardService)
		// 	if (adRewardService instanceof WebAdRewardService) {
		// 		adRewardService.initialize(environment.production)
		// 	}
		// }),
	],
}

// Excluded from SSR
export const appConfig = mergeApplicationConfig(appCommonConfig, {
	providers: [
		// SENTRY
		{
			provide: ErrorHandler,
			useClass: ChunkErrorHandler,
		},
		// SENTRY
		provideEnvironmentInitializer(() => inject(TrackingService)),
		provideEnvironmentInitializer(() => inject(EventService)),
		// Captures utm_*/fbclid/gclid from the landing URL before any navigation can strip them.
		provideEnvironmentInitializer(() => inject(AcquisitionRepository)),
	],
})
